import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Mention from '@tiptap/extension-mention';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { ReactRenderer } from '@tiptap/react';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import { MentionList } from '@/components/kb/MentionList';

// Mention suggestion configuration
const mentionSuggestion = {
  items: async ({ query }: { query: string }) => {
    if (!query.trim()) return [];

    try {
      // Use our API client for fetching users
      const { users } = await import('@/lib/kbApi');
      const data = await users.search(query);

      return (data.users || [])
        .filter((user: any) =>
          user.name.toLowerCase().includes(query.toLowerCase()) ||
          user.email.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 10);
    } catch (error) {
      console.warn('Error fetching mention suggestions:', error);
      return [];
    }
  },

  render: () => {
    let component: ReactRenderer<any>;
    let popup: TippyInstance[];

    return {
      onStart: (props: any) => {
        component = new ReactRenderer(MentionList, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          hideOnClick: 'toggle',
          maxWidth: 300,
          zIndex: 9999,
        });
      },

      onUpdate(props: any) {
        component?.updateProps(props);

        if (!props.clientRect) {
          return;
        }

        popup?.[0]?.setProps({
          getReferenceClientRect: props.clientRect,
        });
      },

      onKeyDown(props: any) {
        if (props.event.key === 'Escape') {
          popup?.[0]?.hide();
          return true;
        }

        return component?.ref?.onKeyDown?.(props) || false;
      },

      onExit() {
        popup?.[0]?.destroy();
        component?.destroy();
      },
    };
  },
};

// Canonical TipTap configuration for RealtyHaus Knowledge Base
export const getTiptapExtensions = () => [
  StarterKit.configure({
    heading: {
      levels: [1, 2, 3, 4, 5, 6],
    },
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
      HTMLAttributes: {
        class: 'tiptap-bullet-list',
      },
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false,
      HTMLAttributes: {
        class: 'tiptap-ordered-list',
      },
    },
    listItem: {
      HTMLAttributes: {
        class: 'tiptap-list-item',
      },
    },
  }),
  TextStyle,
  Color.configure({
    types: ['textStyle'],
  }),
  TextAlign.configure({
    types: ['heading', 'paragraph'],
    alignments: ['left', 'center', 'right', 'justify'],
  }),
  Underline,
  Strike,
  Link.configure({
    openOnClick: false,
    HTMLAttributes: {
      class: 'text-primary underline underline-offset-2 hover:text-primary/80 transition-colors',
    },
  }),
  Image.configure({
    HTMLAttributes: {
      class: 'rounded-lg max-w-full h-auto',
    },
  }),
  Placeholder.configure({
    placeholder: ({ node }) => {
      if (node.type.name === 'heading') {
        return 'What\'s the title?';
      }
      return 'Write your article content here...';
    },
  }),
  TaskList.configure({
    HTMLAttributes: {
      class: 'task-list',
    },
  }),
  TaskItem.configure({
    HTMLAttributes: {
      class: 'task-item',
    },
    nested: true,
  }),
  Mention.configure({
    HTMLAttributes: {
      class: 'mention bg-primary/10 text-primary px-1 py-0.5 rounded font-medium',
    },
    suggestion: mentionSuggestion,
  }),
];

// Editor attributes for consistent styling
export const tiptapEditorAttributes = {
  class: 'prose-editor focus:outline-none min-h-[500px]',
  spellcheck: 'false',
};

// Generate text excerpt from TipTap content
export function generateExcerpt(content: string): string {
  // Remove HTML tags and get first 150 characters
  const text = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > 150 ? text.substring(0, 147) + '...' : text;
}